import ApiRoutes from '@lib/routes/ApiRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import type { SearchClient } from 'instantsearch.js'
import { useMemo } from 'react'

export const useSearchClient = (): SearchClient => {
  // useMemo for a stable reference to make InstantSearch happy
  return useMemo<SearchClient>(
    () => ({
      search(requests) {
        const params = new URLSearchParams()
        params.append('requests', JSON.stringify(requests))

        // Check if any request includes aroundLatLngViaIp
        const hasLocationRequest = requests.some(
          (request) => request.params && 'aroundLatLngViaIP' in request.params,
        )

        return fetch(
          `${absoluteUrl(ApiRoutes.v1.algolia.search)}?${params.toString()}`,
          {
            cache: hasLocationRequest ? 'no-store' : 'force-cache',
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'GET',
          },
        ).then((res) => {
          if (!res.ok) {
            throw new Error('Search request failed')
          }
          return res.json()
        })
      },
      searchForFacetValues(requests) {
        // Return a rejected promise if no requests provided
        if (!requests || requests.length === 0) {
          return Promise.reject(
            new Error('No searchForFacetValues requests provided'),
          )
        }

        // Handle a single request for now, as this is most common
        // For multi-request in the future, would need to implement batching
        const request = requests[0]

        // Validate the request has required properties
        if (!request?.indexName || !request?.params?.facetName) {
          return Promise.reject(
            new Error('Invalid searchForFacetValues request structure'),
          )
        }

        // Extract searchParameters from the request
        const { facetName, facetQuery, ...searchParameters } = request.params

        const params = new URLSearchParams()
        params.append('indexName', request.indexName)
        params.append('facetName', facetName)

        if (facetQuery) {
          params.append('facetQuery', facetQuery)
        }
        params.append('searchParameters', JSON.stringify(searchParameters))

        return fetch(
          `${absoluteUrl(ApiRoutes.v1.algolia.searchForFacetValues)}?${params.toString()}`,
          {
            cache: 'force-cache',
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'GET',
          },
        ).then((res) => {
          if (!res.ok) {
            throw new Error('SearchForFacetValues request failed')
          }
          return res.json().then((response) => [response]) // Wrap in array to match expected return type
        })
      },
    }),
    [],
  )
}
